import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

const BADGE = { pending: "bg-amber-100 text-amber-700", confirmed: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-100 text-slate-600" };

export default function Appointments() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const isStaff = hasRole("direction", "administration", "coordinateur");
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ with_id: "", date: "", reason: "" });

  const load = async () => {
    const { data } = await api.get("/appointments"); setRows(data);
    try { const c = await api.get("/messages-contacts"); setContacts(c.data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/appointments", form); toast.success(t("save")); setOpen(false); setForm({ with_id: "", date: "", reason: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.put(`/appointments/${id}`, { status }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("appointments")}</h1>
        <Button data-testid="appt-new-btn" onClick={() => setOpen(true)} className="rounded-full gap-2"><Plus className="h-4 w-4" />{t("add")}</Button>
      </div>
      <div className="grid gap-3">
        {rows.length === 0 ? <Card className="card-float p-10 text-center text-slate-400 border-slate-200">{t("noData")}</Card> :
          rows.map((a) => (
            <Card key={a.id} data-testid="row-appointments" className="card-float p-4 border-slate-200 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800">{a.reason || "—"}</div>
                <div className="text-sm text-slate-500">{a.requester_name} · {String(a.date).slice(0, 16).replace("T", " ")}</div>
              </div>
              <Badge className={`${BADGE[a.status]} border-0`}>{t(a.status) !== a.status ? t(a.status) : a.status}</Badge>
              {isStaff && a.status === "pending" && (
                <div className="flex gap-1">
                  <Button data-testid="appt-confirm" variant="ghost" size="icon" onClick={() => setStatus(a.id, "confirmed")}><Check className="h-4 w-4 text-emerald-600" /></Button>
                  <Button data-testid="appt-cancel" variant="ghost" size="icon" onClick={() => setStatus(a.id, "cancelled")}><X className="h-4 w-4 text-red-500" /></Button>
                </div>
              )}
            </Card>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{t("appointments")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("recipients")}</label>
              <Select value={form.with_id} onValueChange={(v) => setForm({ ...form, with_id: v })}>
                <SelectTrigger data-testid="appt-with"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("date")}</label><Input data-testid="appt-date" type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("reason")}</label><Textarea data-testid="appt-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button data-testid="appt-save-btn" onClick={create}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
